import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useReasonGate, useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { BusyIcon, LoaderSymbol } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror the API contracts in AlpacaIntelligence.Application.MorTemplate)
// ─────────────────────────────────────────────────────────────────────────────
interface TemplateRow {
  oid:          string;
  templateID:   string | null;
  templateName: string | null;
  reportName:   string | null;
  categoryName: string | null;
  frequency:    string | null;
  startDate:    string | null;
  endDate:      string | null;
  active:       boolean;
}
interface SiteRow {
  oid: string; siteId: string | null; siteName: string | null; description: string | null;
  address: string | null; samplingAddress: string | null; pwsid: string | null;
  siteCode: string | null; siteNumber: string | null; sampleType: string | null;
}
interface ParamRow {
  oid: string; parameterName: string | null; methodName: string | null;
  synonym: string | null; matrix: string | null; test: string | null; components: string | null;
  rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null;
  recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
}
interface CategoryOpt { id: string; name: string; }
interface TemplateDetail {
  oid:          string;
  templateID:   string | null;
  templateName: string | null;
  reportName:   string | null;
  categoryId:   string | null;
  categoryName: string | null;
  frequency:    string | null;
  startDate:    string | null;
  endDate:      string | null;
  active:       boolean;
  retireDate:   string | null;
  emailTo:      string | null;
  comment:      string | null;
  sampleSites:  SiteRow[];
  parameters:   ParamRow[];
}
interface Draft {
  templateName: string;
  reportName:   string;
  categoryId:   string;
  frequency:    string;
  startDate:    string;   // yyyy-MM-dd
  endDate:      string;   // yyyy-MM-dd
  active:       boolean;
  retireDate:   string;   // yyyy-MM-dd
  emailTo:      string;
  comment:      string;
}
type Mode = 'view' | 'edit' | 'add';
type PickerKind = 'site' | 'param' | null;

const EMAIL_RE = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/;
const dateInput = (s?: string | null) => (s ? s.slice(0, 10) : '');
const emptyDraft = (): Draft => ({
  templateName: '', reportName: '', categoryId: '', frequency: '',
  startDate: '', endDate: '', active: true, retireDate: '', emailTo: '', comment: '',
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function MorTemplateSetupPage() {
  const notify  = useNotify();
  const runSave = useSaveFlow();
  const gateReason = useReasonGate();
  const { confirm } = useConfirm();

  const [rows,    setRows]    = useState<TemplateRow[]>([]);
  const [cats,    setCats]    = useState<CategoryOpt[]>([]);
  const [reportNames, setReportNames] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loadMs,  setLoadMs]  = useState<number | null>(null);

  // Full-page detail (add / edit / view) — replaces the list when open.
  const [open,    setOpen]    = useState(false);
  const [mode,    setMode]    = useState<Mode>('view');
  const [current, setCurrent] = useState<TemplateDetail | null>(null);
  const [draft,   setDraft]   = useState<Draft>(emptyDraft());
  const [sites,   setSites]   = useState<SiteRow[]>([]);
  const [params,  setParams]  = useState<ParamRow[]>([]);
  const [saving,  setSaving]  = useState(false);
  const [busy,    setBusy]    = useState(false);   // loading a detail record

  // Add picker (sample site / parameter lookup) — a full data grid like ALPACA:
  // the whole active list is loaded once (cached) and the shared AppDataGrid does
  // paging / column filters / select-all in the browser.
  const [picker,    setPicker]    = useState<PickerKind>(null);
  const [pickSearch, setPickSearch] = useState('');          // global search box
  const [pickLoad,  setPickLoad]  = useState(false);
  const [allSites,  setAllSites]  = useState<SiteRow[]>([]);  // full active list (cached per session)
  const [allParams, setAllParams] = useState<ParamRow[]>([]);
  // Rows ticked in the picker (by oid) — added together when the user clicks OK.
  const [pickSel,   setPickSel]   = useState<Set<string | number>>(new Set());

  // Test action (legacy btn_DWQRTestData) — date-range popup
  const [testOpen, setTestOpen] = useState(false);
  const [testFrom, setTestFrom] = useState('');
  const [testTo,   setTestTo]   = useState('');
  const [testBusy, setTestBusy] = useState(false);

  // ── Fetch list + category lookups ───────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const [listRes, catRes, rnRes] = await Promise.all([
        api.get('/api/v1/mor-template'),
        api.get('/api/v1/mor-template/lookups/categories'),
        api.get('/api/v1/mor-template/lookups/report-names'),
      ]);
      setRows((listRes.data as { data: TemplateRow[] }).data ?? []);
      setCats((catRes.data as { data: CategoryOpt[] }).data ?? []);
      setReportNames((rnRes.data as { data: { value: string; label: string }[] }).data ?? []);
    } catch (e: unknown) {
      setError(apiErr(e, 'Failed to load MOR templates'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(r =>
      [r.templateID, r.templateName, r.reportName, r.categoryName, r.frequency]
        .some(v => (v ?? '').toLowerCase().includes(t)));
  }, [rows, search]);

  const cols: AppColumn<TemplateRow>[] = [
    { field: 'templateID',   header: 'Template ID',  width: 130 },
    { field: 'templateName', header: 'Template Name', width: 240 },
    { field: 'reportName',   header: 'Report Name',  width: 220 },
    { field: 'categoryName', header: 'Category',     width: 150 },
    { field: 'frequency',    header: 'Frequency',    width: 130 },
    { field: 'startDate',    header: 'Start Date',   width: 130, type: 'date' },
    { field: 'endDate',      header: 'End Date',     width: 130, type: 'date' },
    { field: 'active',       header: 'Active',       width: 110, type: 'status',
      text: r => (r.active ? 'Active' : 'Retired') },
  ];

  // ── Detail open/close ────────────────────────────────────────────────────────
  const draftFromDetail = (d: TemplateDetail): Draft => ({
    templateName: d.templateName ?? '', reportName: d.reportName ?? '',
    categoryId: d.categoryId ?? '', frequency: d.frequency ?? '',
    startDate: dateInput(d.startDate), endDate: dateInput(d.endDate),
    active: d.active, retireDate: dateInput(d.retireDate),
    emailTo: d.emailTo ?? '', comment: d.comment ?? '',
  });
  const applyDetail = (d: TemplateDetail, m: Mode) => {
    setCurrent(d); setDraft(draftFromDetail(d));
    setSites(d.sampleSites ?? []); setParams(d.parameters ?? []); setMode(m);
  };

  async function loadDetail(oid: string): Promise<TemplateDetail | null> {
    try {
      const res = await api.get(`/api/v1/mor-template/${oid}`);
      return (res.data as { data: TemplateDetail }).data;
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Could not load template detail.'));
      return null;
    }
  }

  async function openDetail(row: TemplateRow) {
    setBusy(true); setOpen(true); setMode('view'); setCurrent(null);
    const d = await loadDetail(row.oid);
    setBusy(false);
    if (!d) { setOpen(false); return; }
    applyDetail(d, 'view');
  }
  function openAdd() {
    setCurrent(null); setDraft(emptyDraft()); setSites([]); setParams([]);
    setMode('add'); setOpen(true);
  }
  async function openEditSelected() {
    if (selected.size !== 1) { notify.warning('Select exactly one row to edit.'); return; }
    const oid = String(Array.from(selected)[0]);
    setBusy(true); setOpen(true); setMode('edit'); setCurrent(null);
    const d = await loadDetail(oid);
    setBusy(false);
    if (!d) { setOpen(false); return; }
    applyDetail(d, 'edit');
  }
  function closeDetail() {
    setOpen(false); setCurrent(null); setDraft(emptyDraft());
    setSites([]); setParams([]); setPicker(null); setPickSel(new Set()); setTestOpen(false);
  }

  const editable = mode === 'add' || mode === 'edit';

  // ── Child collections (link / remove) ───────────────────────────────────────
  // Bulk remove (ALPACA "Remove" toolbar button — removes the ticked child rows).
  function removeSites(oids: string[])  { setSites(s => s.filter(x => !oids.includes(x.oid))); }
  function removeParams(oids: string[]) { setParams(p => p.filter(x => !oids.includes(x.oid))); }

  function openPicker(kind: PickerKind) {
    setPicker(kind); setPickSearch(''); setPickSel(new Set());
    void loadAll(kind);
  }
  // Load the FULL active list once and cache it for the session — the grid
  // pages / filters it client-side (the active set is ~9k sites / ~4k params,
  // within the 10k-row grid target; the endpoint is output-cached server-side).
  const loadAll = useCallback(async (kind: PickerKind) => {
    if (!kind) return;
    if (kind === 'site' && allSites.length > 0) return;
    if (kind === 'param' && allParams.length > 0) return;
    setPickLoad(true);
    try {
      const path = kind === 'site' ? 'sample-sites/all' : 'test-parameters/all';
      const res = await api.get(`/api/v1/mor-template/${path}`);
      const data = (res.data as { data: (SiteRow | ParamRow)[] }).data ?? [];
      if (kind === 'site') setAllSites(data as SiteRow[]);
      else setAllParams(data as ParamRow[]);
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Failed to load list.'));
    } finally {
      setPickLoad(false);
    }
  }, [allSites.length, allParams.length, notify]);

  function addFromPicker(row: SiteRow | ParamRow) {
    if (picker === 'site') {
      const s = row as SiteRow;
      setSites(prev => prev.some(x => x.oid === s.oid) ? prev : [...prev, s]);
    } else {
      const p = row as ParamRow;
      setParams(prev => prev.some(x => x.oid === p.oid) ? prev : [...prev, p]);
    }
  }
  // OK → add every ticked row at once (dedupes against already-linked rows).
  function addSelected() {
    const pool: (SiteRow | ParamRow)[] = picker === 'site' ? allSites : allParams;
    pool.filter(r => pickSel.has(r.oid)).forEach(addFromPicker);
    setPicker(null); setPickSel(new Set());
  }
  // Global search box → narrow the full list before the grid pages it.
  const pickFiltered = useMemo<(SiteRow | ParamRow)[]>(() => {
    if (!picker) return [];
    const pool: (SiteRow | ParamRow)[] = picker === 'site' ? allSites : allParams;
    const term = pickSearch.trim().toLowerCase();
    if (!term) return pool;
    return pool.filter(r =>
      Object.values(r).some(v => typeof v === 'string' && v.toLowerCase().includes(term)));
  }, [picker, allSites, allParams, pickSearch]);

  // ── Test action (legacy btn_DWQRTestData) ───────────────────────────────────
  function openTest() {
    if (sites.length === 0 || params.length === 0) {
      notify.warning('Add at least one Sample Location and one Test Parameter before testing.');
      return;
    }
    setTestFrom(draft.startDate || ''); setTestTo(draft.endDate || ''); setTestOpen(true);
  }
  async function runTest() {
    // ALPACA's Test RENDERS the report (PDF preview), not a count — for the CURRENT
    // form selection (Report × sites × parameters × date range), no save required.
    if (!draft.reportName) { notify.warning('Select a Report before testing.'); return; }
    if (sites.length === 0 || params.length === 0) { notify.warning('Add at least one Sample Location and one Parameter.'); return; }
    if (!testFrom || !testTo) { notify.warning('Enter Date From and Date To.'); return; }
    if (testBusy) return;
    setTestBusy(true);
    try {
      const res = await api.post('/api/v1/mor-template/test-report', {
        reportName:    draft.reportName,
        sampleSiteIds: sites.map(s => s.oid),
        parameterIds:  params.map(p => p.oid),
        from: testFrom, to: testTo,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank');           // show the rendered report PDF
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setTestOpen(false);
    } catch (e: unknown) {
      notify.error(await blobErr(e, 'Test failed.'));
    } finally {
      setTestBusy(false);
    }
  }

  // ── Save / delete ───────────────────────────────────────────────────────────
  function validate(): string | null {
    if (!draft.templateName.trim()) return 'Template Name is required.';
    if (!draft.reportName.trim())   return 'Report Name is required.';
    if (draft.emailTo.trim()) {
      const bad = draft.emailTo.split(',').map(e => e.trim()).filter(e => e && !EMAIL_RE.test(e));
      if (bad.length) return `Invalid email format: ${bad.join(', ')}`;
    }
    return null;
  }
  function buildPayload() {
    return {
      templateName: draft.templateName.trim(),
      reportName:   draft.reportName.trim(),
      categoryId:   draft.categoryId || null,
      frequency:    draft.frequency.trim() || null,
      startDate:    draft.startDate || null,
      endDate:      draft.endDate || null,
      active:       draft.active,
      retireDate:   draft.retireDate || null,
      emailTo:      draft.emailTo.trim() || null,
      comment:      draft.comment.trim() || null,
      sampleSiteIds: sites.map(s => s.oid),
      parameterIds:  params.map(p => p.oid),
    };
  }
  /** Save the template.  closeAfter=true returns to the list (Save & Close);
   *  false keeps the form open on the saved record (Save). */
  async function handleSave(closeAfter: boolean) {
    const err = validate();
    if (err) { notify.warning(err); return; }
    if (saving) return;
    const editing = mode === 'edit' && current;
    setSaving(true);
    try {
      await runSave({
        // Reason-for-change gate (standing rule 11) — only on an edit (Create exempt).
        formName: 'MOR Template Setup',
        auditAction: editing ? 'edit' : 'create',
        save: async () => {
          let oid = current?.oid ?? '';
          if (editing) {
            await api.put(`/api/v1/mor-template/${current!.oid}`, buildPayload());
          } else {
            const res = await api.post('/api/v1/mor-template', buildPayload());
            oid = (res.data as { data: { id: string } }).data.id;
          }
          if (!closeAfter && oid) {
            const d = await loadDetail(oid);
            if (d) applyDetail(d, 'edit');
          }
        },
        successMessage: editing ? 'Template updated successfully.' : 'Template created successfully.',
        onSaved: async () => { if (closeAfter) { closeDetail(); setSelected(new Set()); await load(); } else { await load(); } },
        onError: (e) => notify.error(`Save failed: ${apiErr(e, 'Could not save template.')}`),
      });
    } finally {
      setSaving(false);
    }
  }
  async function handleDelete() {
    if (!current) return;
    if (!(await confirm({ title: 'Delete template?', message: `Delete template "${current.templateName}" (${current.templateID})?`, tone: 'danger', confirmLabel: 'Delete' }))) return;
    const reason = await gateReason('MOR Template Setup', 'delete');
    if (reason === null) return;
    if (saving) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => api.delete(`/api/v1/mor-template/${current.oid}`));
      closeDetail(); setSelected(new Set()); await load();
      notify.success('Template deleted.');
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete template.')}`);
    } finally {
      setSaving(false);
    }
  }
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.oid));
    if (!(await confirm({
      title: targets.length === 1 ? 'Delete template?' : 'Delete templates?',
      message: `Delete ${targets.length} template${targets.length === 1 ? '' : 's'}? (${targets.map(t => t.templateID).join(', ')})`,
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    const reason = await gateReason('MOR Template Setup', 'delete');
    if (reason === null) return;
    if (saving) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => Promise.all(targets.map(t => api.delete(`/api/v1/mor-template/${t.oid}`))));
      setSelected(new Set()); await load();
      notify.success(`Deleted ${targets.length} template${targets.length === 1 ? '' : 's'}.`);
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete one or more templates.')}`);
      await load();
    } finally {
      setSaving(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Overlays that sit above the DETAIL view: the Add picker (sample site /
  // parameter lookup) and the Test date-range dialog.  The record detail itself
  // is a FULL-PAGE view (below) — never a popup.
  // ─────────────────────────────────────────────────────────────────────────────
  // Action bar — rendered at TOP and BOTTOM of the full-page detail (identical
  // set, BO UI Standard §4).  Save = solid 💾; Cancel/Close pushed right.
  const detailActionBar = (bottom?: boolean) => (
    <div className={`mt-actionbar${bottom ? ' bottom' : ''}`}>
      {editable ? (
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => handleSave(false)} disabled={saving || busy}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="ca-btn" onClick={() => handleSave(true)} disabled={saving || busy}>
            <span className="tb-i">💾</span> Save &amp; Close
          </button>
          <button className="ca-btn" onClick={openTest} disabled={busy}><i className="ti ti-checkup-list" /> Test</button>
          <button className="ca-btn mt-abtn-right" onClick={closeDetail} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      ) : (
        <>
          <button className="ca-btn" onClick={() => setMode('edit')} disabled={busy}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <button className="ca-btn" onClick={handleDelete} disabled={saving || busy}>
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
          <button className="ca-btn" onClick={openTest} disabled={busy}><i className="ti ti-checkup-list" /> Test</button>
          <button className="ca-btn mt-abtn-right" onClick={closeDetail}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // DETAIL  (New / Edit / View) — a FULL-PAGE view that REPLACES the list
  // (BO UI Standard §4, standing rule 12 — superseded the earlier right-sized
  // popup on 2026-07-01).  Bespoke layout (not the shared SettingsDetailPage)
  // because the Sample Locations / Parameters child grids and the Test action
  // don't fit the CrudField model; anatomy follows the standard: view-then-edit,
  // action bar top + bottom, Save solid / Cancel.
  // ─────────────────────────────────────────────────────────────────────────────
  if (open) {
    return (
      <>
        <style>{MT_CSS}</style>
        <div className="bl-page">
          <div className="bl-grid-area">
            <div className="bl-card bl-card-flex">
              <div className="section-hdr mt-detail-hdr">
                <FormTitle pageId="mor-template" title={`MOR Template Setup — ${mode === 'add' ? 'New' : (current?.templateID ?? '')}`}
                  auditFormName="MOR Template Setup" auditSourceId={current?.oid || undefined} />
              </div>
              {detailActionBar()}

              <div className="mt-detail-body">
                {busy ? (
                  <div className="mt-busy"><LoaderSymbol size={14} /> Loading…</div>
                ) : (
                  <>
                    <div className="mt-section-title">General Information</div>
                    {/* 3-column layout (row-major) matching the legacy ALPACA MOR
                        Template Setup form:
                          Template ID   | Category    | Frequency
                          Template Name | Start Date  | End Date
                          Report Name   | Retire Date | Email To
                          Active        | Comment */}
                    <div className="mt-grid">
                      {/* Row 1 */}
                      <Field label="Template ID">
                        <div className="mt-val">{current?.templateID || 'Autogenerate'}</div>
                      </Field>
                      <Field label="Category">
                        {editable
                          ? (
                            <select value={draft.categoryId} onChange={e => setDraft(d => ({ ...d, categoryId: e.target.value }))}>
                              <option value="">— None —</option>
                              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          )
                          : <div className="mt-val">{current?.categoryName || '—'}</div>}
                      </Field>
                      <Field label="Frequency">
                        {editable
                          ? <input value={draft.frequency} onChange={e => setDraft(d => ({ ...d, frequency: e.target.value }))} placeholder="e.g. Monthly" />
                          : <div className="mt-val">{current?.frequency || '—'}</div>}
                      </Field>

                      {/* Row 2 */}
                      <Field label="Template Name *">
                        {editable
                          ? <input autoFocus value={draft.templateName} onChange={e => setDraft(d => ({ ...d, templateName: e.target.value }))} placeholder="e.g. DWOL Monthly Report" />
                          : <div className="mt-val"><strong>{current?.templateName || '—'}</strong></div>}
                      </Field>
                      <Field label="Start Date">
                        {editable
                          ? <input type="date" value={draft.startDate} onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))} />
                          : <div className="mt-val">{current?.startDate ? fmtLocalDate(current.startDate) : '—'}</div>}
                      </Field>
                      <Field label="End Date">
                        {editable
                          ? <input type="date" value={draft.endDate} onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))} />
                          : <div className="mt-val">{current?.endDate ? fmtLocalDate(current.endDate) : '—'}</div>}
                      </Field>

                      {/* Row 3 */}
                      <Field label="Report Name *">
                        {editable
                          ? (
                            <select value={draft.reportName} onChange={e => setDraft(d => ({ ...d, reportName: e.target.value }))}>
                              <option value="">— Select —</option>
                              {/* Keep an already-saved value visible even if its
                                  package/report was since retired. */}
                              {draft.reportName && !reportNames.some(r => r.value === draft.reportName) && (
                                <option value={draft.reportName}>{draft.reportName}</option>
                              )}
                              {reportNames.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                          )
                          : <div className="mt-val">{current?.reportName || '—'}</div>}
                      </Field>
                      <Field label="Retire Date">
                        {editable
                          ? <input type="date" value={draft.retireDate} onChange={e => setDraft(d => ({ ...d, retireDate: e.target.value }))} />
                          : <div className="mt-val">{current?.retireDate ? fmtLocalDate(current.retireDate) : '—'}</div>}
                      </Field>
                      <Field label="Email To">
                        {editable
                          ? <input value={draft.emailTo} onChange={e => setDraft(d => ({ ...d, emailTo: e.target.value }))} placeholder="comma-separated emails" />
                          : <div className="mt-val">{current?.emailTo || '—'}</div>}
                      </Field>

                      {/* Row 4 */}
                      <Field label="Active">
                        {editable
                          ? (
                            <label className="mt-check">
                              <input type="checkbox" checked={draft.active} onChange={e => setDraft(d => ({ ...d, active: e.target.checked }))} />
                              <span>{draft.active ? 'Active' : 'Retired'}</span>
                            </label>
                          )
                          : <div className="mt-val">{current?.active ? 'Active' : 'Retired'}</div>}
                      </Field>
                      <Field label="Comment">
                        {editable
                          ? <input value={draft.comment} onChange={e => setDraft(d => ({ ...d, comment: e.target.value }))} />
                          : <div className="mt-val">{current?.comment || '—'}</div>}
                      </Field>
                    </div>

                    <div className="mt-children">
                      <ChildPanel
                        title="Sample Locations" icon="ti-map-pin" count={sites.length}
                        editable={editable} onAdd={() => openPicker('site')} onRemoveMany={removeSites}
                        head={['SiteName', 'Description', 'Address', 'SamplingAddress', 'PWSID', 'SiteCode', 'SiteNumber', 'SampleType']}
                        rows={sites.map(s => ({
                          key: s.oid,
                          cells: [s.siteName ?? '', s.description ?? '', s.address ?? '', s.samplingAddress ?? '', s.pwsid ?? '', s.siteCode ?? '', s.siteNumber ?? s.siteId ?? '', s.sampleType ?? ''],
                        }))}
                        emptyText="No sample locations linked." />
                      <ChildPanel
                        title="Parameters" icon="ti-test-pipe" count={params.length}
                        editable={editable} onAdd={() => openPicker('param')} onRemoveMany={removeParams}
                        head={['Parameter', 'Synonym', 'Matrix', 'Test', 'Method', 'Components', 'RPDSigFig', 'RPDCutOff', 'RPDDecimal', 'RECSigFig', 'RECCutOff', 'RECDecimal']}
                        rows={params.map(p => ({
                          key: p.oid,
                          cells: [p.parameterName ?? '', p.synonym ?? '', p.matrix ?? '', p.test ?? '', p.methodName ?? '', p.components ?? '', p.rpdSigFig ?? '', p.rpdCutOff ?? '', p.rpdDecimal ?? '', p.recSigFig ?? '', p.recCutOff ?? '', p.recDecimal ?? ''],
                        }))}
                        emptyText="No parameters linked." />
                    </div>
                  </>
                )}
              </div>

              {detailActionBar(true)}
            </div>
          </div>
        </div>

        {picker && (
          <PickerOverlay
            picker={picker} rows={pickFiltered} pickSearch={pickSearch} pickLoad={pickLoad}
            onSearch={setPickSearch}
            selectedIds={pickSel} onSelectionChange={setPickSel}
            onAddSelected={addSelected}
            onClose={() => { setPicker(null); setPickSel(new Set()); }} />
        )}

        {testOpen && (
          <div className="mt-overlay mt-overlay-2" onClick={e => { if (e.target === e.currentTarget) setTestOpen(false); }}>
            <div className="mt-test">
              <div className="mt-test-hdr"><span><i className="ti ti-checkup-list" /> Test — Date Range</span>
                <button className="mt-x" onClick={() => setTestOpen(false)}>×</button></div>
              <div className="mt-test-body">
                <p className="mt-test-note">Renders the <strong>{draft.reportName || 'selected'}</strong> report for this template's {sites.length} location(s) × {params.length} parameter(s) over the chosen collected-date range, and opens it in a new tab.</p>
                <div className="mt-test-row">
                  <label>Date From<input type="date" value={testFrom} onChange={e => setTestFrom(e.target.value)} /></label>
                  <label>Date To<input type="date" value={testTo} onChange={e => setTestTo(e.target.value)} /></label>
                </div>
              </div>
              <div className="mt-test-ftr">
                <button className="mt-btn mt-btn-primary" onClick={runTest} disabled={testBusy}>
                  <BusyIcon busy={testBusy} icon="ti-player-play" /> {testBusy ? 'Testing…' : 'Run Test'}
                </button>
                <button className="mt-btn mt-btn-secondary" onClick={() => setTestOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // LIST
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{MT_CSS}</style>
      <div className="bl-page">
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">MOR Template Setup</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page,
                with the selection pill as a title-row chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="mor-template" loadMs={loadMs} loading={loading} auditFormName="MOR Template Setup" />
              {selected.size > 0 && (
                <span className="mt-sel-pill" style={{ marginLeft: 10 }}>
                  <i className="ti ti-circle-check" /> {selected.size} selected
                  <button className="mt-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                </span>
              )}
            </div>
            <div className="dp-subtitle">
              Reusable MOR / DWQR report templates · Source: BTLIMS <code>DWQRReportTemplateSetup</code>
            </div>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row ON the grey page (BO UI Standard v2) —
              canonical order New · Edit · Delete · Refresh · Export to. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              newLabel="New"
              onNew={openAdd}
              onEdit={openEditSelected}
              editDisabled={selected.size !== 1}
              onDelete={deleteSelected}
              deleteDisabled={selected.size === 0}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'mor-templates', ...gridExportMatrix(filtered, cols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
            </div>
            {filtered.length !== rows.length && (
              <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            )}
          </div>

          {/* ONE white grid card — grid + its single footer only. */}
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, var(--ai-orange))', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              <AppDataGrid<TemplateRow>
                rows={filtered}
                columns={cols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowClick={openDetail}
                emptyText="No MOR templates found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Picker grid columns — mirror the legacy ALPACA "Sample Sites" / parameter
// lookup grids.  (dbo.SampleSites has a single Address column in this DB.)
const SITE_PICKER_COLS: AppColumn<SiteRow>[] = [
  { field: 'siteName',        header: 'SiteName' },
  { field: 'description',     header: 'Description' },
  { field: 'address',         header: 'Address' },
  { field: 'samplingAddress', header: 'SamplingAddress' },
  { field: 'pwsid',           header: 'PWSID' },
  { field: 'siteCode',        header: 'SiteCode' },
  { field: 'siteNumber',      header: 'SiteNumber' },
  { field: 'sampleType',      header: 'SampleType' },
];
// Parameter PICKER shows only the legacy lookup columns (the RPD/REC columns
// live on the linked detail grid, not the picker).
const PARAM_PICKER_COLS: AppColumn<ParamRow>[] = [
  { field: 'parameterName', header: 'Parameter' },
  { field: 'synonym',       header: 'Synonym' },
  { field: 'matrix',        header: 'Matrix' },
  { field: 'test',          header: 'Test' },
  { field: 'methodName',    header: 'Method' },
  { field: 'components',    header: 'Component' },
];

// ── Add picker overlay — a full data grid (ALPACA Sample Sites lookup parity):
// shared AppDataGrid with row-selection checkboxes (per-row + select-all),
// column filters, pagination, and a global search box; OK adds every ticked row.
function PickerOverlay({ picker, rows, pickSearch, pickLoad, onSearch, selectedIds, onSelectionChange, onAddSelected, onClose }: {
  picker: 'site' | 'param';
  rows: (SiteRow | ParamRow)[];
  pickSearch: string; pickLoad: boolean;
  onSearch: (v: string) => void;
  selectedIds: Set<string | number>;
  onSelectionChange: (ids: Set<string | number>) => void;
  onAddSelected: () => void;
  onClose: () => void;
}) {
  const isSite = picker === 'site';
  return (
    <div className="mt-overlay mt-overlay-2" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mt-picker mt-picker-grid">
        <div className="mt-picker-hdr">
          <span><i className={`ti ${isSite ? 'ti-map-pin' : 'ti-test-pipe'}`} /> Add {isSite ? 'Sample Location' : 'Test Parameter'}</span>
          <button className="mt-x" onClick={onClose}>×</button>
        </div>
        <div className="mt-picker-search">
          <i className="ti ti-search" />
          <input autoFocus value={pickSearch} onChange={e => onSearch(e.target.value)} placeholder={`Search ${isSite ? 'sample locations' : 'parameters'}…`} />
        </div>
        <div className="mt-picker-gridbody">
          {isSite ? (
            <AppDataGrid<SiteRow>
              rows={rows as SiteRow[]} columns={SITE_PICKER_COLS} getRowId={r => r.oid}
              loading={pickLoad} selectable selectedIds={selectedIds} onSelectionChange={onSelectionChange}
              emptyText="No sample locations." />
          ) : (
            <AppDataGrid<ParamRow>
              rows={rows as ParamRow[]} columns={PARAM_PICKER_COLS} getRowId={r => r.oid}
              loading={pickLoad} selectable selectedIds={selectedIds} onSelectionChange={onSelectionChange}
              emptyText="No parameters." />
          )}
        </div>
        <div className="mt-picker-ftr">
          <span className="mt-picker-hint">{selectedIds.size} selected</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="mt-btn mt-btn-primary" disabled={selectedIds.size === 0} onClick={onAddSelected}>
              Add Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </button>
            <button className="mt-btn mt-btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────────────────────
function Field({ label, full, children }: { label: string; full?: boolean; children: ReactNode }) {
  return (
    <div className={`mt-field${full ? ' mt-field-full' : ''}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}

interface ChildRow { key: string; cells: string[]; }
function ChildPanel({ title, icon, count, editable, onAdd, onRemoveMany, head, rows, emptyText }: {
  title: string; icon: string; count: number; editable: boolean;
  onAdd: () => void; onRemoveMany: (keys: string[]) => void;
  head: string[]; rows: ChildRow[]; emptyText: string;
}) {
  // Row selection for the ALPACA-style Add / Remove toolbar.
  const [sel, setSel] = useState<Set<string>>(new Set());
  const keys = rows.map(r => r.key);
  const selKeys = keys.filter(k => sel.has(k));
  const allChecked = rows.length > 0 && selKeys.length === rows.length;
  const someChecked = selKeys.length > 0 && !allChecked;
  const toggle = (k: string) =>
    setSel(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSel(allChecked ? new Set() : new Set(keys));
  const doRemove = () => { onRemoveMany(selKeys); setSel(new Set()); };
  const colSpan = head.length + (editable ? 1 : 0);
  return (
    <div className="mt-child">
      <div className="mt-child-hdr">
        <span><i className={`ti ${icon}`} /> {title} <span className="mt-child-count">{count}</span></span>
        {editable && (
          <div className="mt-child-actions">
            <button className="mt-btn mt-btn-add" onClick={onAdd}><i className="ti ti-plus" /> Add</button>
            <button className="mt-btn mt-btn-add" disabled={selKeys.length === 0} onClick={doRemove}>
              <i className="ti ti-minus" /> Remove{selKeys.length > 0 ? ` (${selKeys.length})` : ''}
            </button>
          </div>
        )}
      </div>
      <div className="mt-child-scroll">
        {/* Column headers ALWAYS render (even with zero rows) — matches the
            legacy ALPACA grids, which show the header row above a centred
            "No data to display" message. */}
        <table className="mt-child-tbl">
          <thead>
            <tr>
              {editable && (
                <th className="mt-child-cb" style={{ width: 32 }}>
                  <input type="checkbox" checked={allChecked} disabled={rows.length === 0}
                    ref={el => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll} aria-label="Select all" />
                </th>
              )}
              {head.map(h => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="mt-child-empty" colSpan={colSpan}>{emptyText}</td>
              </tr>
            ) : (
              rows.map(r => (
                <tr key={r.key} className={sel.has(r.key) ? 'mt-child-rowsel' : undefined}>
                  {editable && (
                    <td className="mt-child-cb">
                      <input type="checkbox" checked={sel.has(r.key)} onChange={() => toggle(r.key)}
                        aria-label="Select row" />
                    </td>
                  )}
                  {r.cells.map((c, i) => <td key={i} title={c}>{c}</td>)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function apiErr(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

// Error from a responseType:'blob' request — the error body is a Blob, so read
// + parse it for the API's { error: { message } } envelope (used by Test render).
async function blobErr(e: unknown, fallback: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch { /* not JSON — fall through */ }
  }
  return apiErr(e, fallback);
}

// ── Scoped styles ───────────────────────────────────────────────────────────────
const MT_CSS = `
  .bl-tbl tbody tr { cursor: pointer; }
  .mt-link { color:var(--ai-blue); cursor:pointer; } .mt-link:hover { text-decoration:underline; }

  .mt-sel-pill { display:inline-flex; align-items:center; gap:6px; background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-hover));
    border:1px solid var(--ai-required-line); padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600; }
  .mt-sel-clear { background:transparent; border:none; color:var(--brand-accent-hover, var(--ai-orange-hover)); cursor:pointer; font-size:14px; line-height:1; padding:0 6px; }
  .mt-sel-clear:hover { color:var(--ai-orange-dark); }

  /* Full-page detail (BO UI Standard §4 — the list is REPLACED, never a popup) */
  .mt-detail-hdr { padding:8px 14px; border-bottom:1px solid var(--ai-line); margin:0; background:var(--ai-header-bg);
    display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
  /* Action bar — rendered TOP and BOTTOM (identical set); Cancel/Close right. */
  .mt-actionbar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; flex-shrink:0;
    padding:9px 14px; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line); }
  .mt-actionbar.bottom { border-bottom:0; border-top:1px solid var(--ai-line); }
  .mt-abtn-right { margin-left:auto; }
  .mt-detail-body { flex:1 1 auto; min-height:0; padding:0 0 18px; overflow:auto; }
  .mt-section-title { font-size:13px; font-weight:700; color:var(--ai-ink); padding:12px 16px 4px; }
  .mt-busy { display:flex; align-items:center; gap:8px; color:var(--ai-muted); font-size:13px; padding:24px; justify-content:center; }

  .mt-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:12px 20px; padding:8px 16px 16px; }
  @media (max-width: 980px){ .mt-grid { grid-template-columns:1fr 1fr; } }
  @media (max-width: 640px){ .mt-grid { grid-template-columns:1fr; } }
  .mt-field { display:flex; flex-direction:column; gap:4px; }
  .mt-field-full { grid-column:1 / -1; }
  /* Title-case labels matching the Method (ModuleCrudDialog .qs-label) convention —
     12px, #5a4a3a, semibold, NO uppercase. */
  .mt-field label { font-size:12px; color:var(--ai-ink); font-weight:600; }
  .mt-field input, .mt-field select { font-size:13px; padding:6px 8px; border:1px solid var(--ai-line); border-radius:4px;
    background:#fff; color:var(--ai-ink); outline:none; }
  .mt-field input:focus, .mt-field select:focus { border-color:var(--brand-accent, var(--ai-orange)); }
  .mt-check { display:inline-flex; align-items:center; gap:8px; font-size:13px; color:var(--ai-ink); padding:4px 0; }
  .mt-val { font-size:13px; padding:6px 0; color:var(--ai-ink); border-bottom:1px solid var(--ai-line); min-height:18px; }
  .mt-val strong { color:var(--ai-ink); }

  /* Stacked full-width (legacy layout): Sample Locations above Parameters, each
     scrolling horizontally so the full column set is reachable. */
  .mt-children { display:flex; flex-direction:column; gap:16px; padding:0 16px; }
  .mt-child { border:1px solid var(--ai-line); border-radius:8px; overflow:hidden; display:flex; flex-direction:column; }
  .mt-child-hdr { display:flex; align-items:center; justify-content:space-between; padding:8px 10px; background:var(--ai-header-bg);
    border-bottom:1px solid var(--ai-line); font-size:12px; font-weight:600; color:var(--ai-ink); }
  .mt-child-count { display:inline-block; min-width:18px; text-align:center; background:var(--ai-blue); color:#fff;
    border-radius:9px; font-size:10px; padding:1px 6px; margin-left:4px; }
  .mt-child-scroll { max-height:260px; overflow:auto; }
  .mt-child-empty { padding:14px; font-size:12px; color:var(--ai-muted); font-style:italic; text-align:center; }
  .mt-child-tbl { width:100%; border-collapse:collapse; font-size:12px; }
  /* Table metrics — EXACT BO UI Standard prototype values (§9); no zebra. */
  .mt-child-tbl th { text-align:left; padding:11px 12px; background:var(--ai-header-bg); color:var(--ai-ink);
    font-size:12px; font-weight:800; position:sticky; top:0;
    border-bottom:1px solid var(--ai-line); border-right:1px solid var(--ai-line-soft); }
  .mt-child-tbl td { font-size:12.5px; padding:10px 12px; border-bottom:1px solid var(--ai-line-soft);
    border-right:1px solid var(--ai-line-soft); color:var(--ai-ink); max-width:200px;
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .mt-child-tbl tbody tr:hover td { background:var(--ai-row-hover); }
  .mt-child-rm button { background:transparent; border:none; color:var(--ai-red); cursor:pointer; font-size:14px; padding:2px 4px; }
  .mt-child-rm button:hover { color:var(--ai-red); }
  .mt-child-actions { display:flex; gap:8px; }
  .mt-child-cb { width:32px; text-align:center; }
  .mt-child-cb input { cursor:pointer; }
  .mt-child-rowsel { background:var(--ai-row-selected); }

  .mt-btn { padding:6px 14px; font-size:12px; font-weight:600; border-radius:4px; cursor:pointer; border:1px solid transparent;
    display:inline-flex; align-items:center; gap:5px; }
  .mt-btn-primary { background:var(--brand-accent, var(--ai-orange)); color:#fff; }
  .mt-btn-primary:hover { background:var(--brand-accent-hover, var(--ai-orange-hover)); }
  .mt-btn-primary:disabled { opacity:.6; cursor:not-allowed; }
  .mt-btn-secondary { background:transparent; color:var(--ai-ink); border-color:transparent; }
  .mt-btn-secondary:hover { background:var(--ai-line-soft); border-color:var(--ai-line); }
  .mt-btn-add { background:#fff; color:var(--brand-accent-hover, var(--ai-orange-hover)); border-color:var(--ai-required-line); padding:3px 10px; font-size:11px; }
  .mt-btn-add:hover { background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); }

  .mt-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center;
    justify-content:center; z-index:9000; backdrop-filter:blur(2px); }
  .mt-overlay-2 { z-index:9100; }
  .mt-x { width:26px; height:26px; border:none; border-radius:4px; background:rgba(255,255,255,.18); color:#fff;
    cursor:pointer; font-size:17px; }
  .mt-x:hover { background:rgba(255,255,255,.32); }

  .mt-picker { width:min(94vw, 520px); max-height:80vh; background:#fff; border-radius:10px; overflow:hidden;
    display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.45); }
  /* Grid-style picker (ALPACA Sample Sites lookup) — wider, grid fills the body. */
  .mt-picker-grid { width:min(96vw, 1180px); height:86vh; max-height:86vh; }
  .mt-picker-gridbody { flex:1; min-height:0; overflow:hidden; display:flex; flex-direction:column; padding:8px 12px; }
  .mt-picker-gridbody > * { flex:1; min-height:0; }
  .mt-picker-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:var(--ai-blue);
    color:#fff; font-size:13px; font-weight:600; }
  .mt-picker-search { display:flex; align-items:center; gap:6px; padding:8px 12px; border-bottom:1px solid var(--ai-line-soft); }
  .mt-picker-search i { color:var(--ai-muted); }
  .mt-picker-search input { flex:1; border:1px solid var(--ai-line); border-radius:4px; padding:6px 8px; font-size:13px; outline:none; }
  .mt-picker-search input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
  .mt-picker-list { flex:1; overflow:auto; min-height:120px; }
  .mt-picker-empty { padding:18px; text-align:center; color:var(--ai-muted); font-size:12px; font-style:italic; }
  .mt-picker-row { width:100%; text-align:left; background:#fff; border:none; border-bottom:1px solid var(--ai-line-soft);
    padding:8px 14px; cursor:pointer; display:flex; flex-direction:column; gap:1px; position:relative; }
  .mt-picker-row:hover:not(:disabled) { background:var(--ai-row-hover); }
  .mt-picker-row:disabled { opacity:.55; cursor:default; }
  .mt-picker-main { font-size:13px; color:var(--ai-ink); font-weight:600; }
  .mt-picker-sub { font-size:11px; color:var(--ai-muted); }
  .mt-picker-added { position:absolute; right:12px; top:50%; transform:translateY(-50%); color:var(--ai-green); }
  .mt-picker-ftr { display:flex; align-items:center; justify-content:space-between; padding:8px 14px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line-soft); }
  .mt-picker-hint { font-size:11px; color:var(--ai-muted); }

  .mt-test { width:min(94vw, 460px); background:#fff; border-radius:10px; overflow:hidden;
    display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.45); }
  .mt-test-hdr { display:flex; align-items:center; justify-content:space-between; padding:10px 16px; background:var(--ai-blue); color:#fff; font-size:13px; font-weight:600; }
  .mt-test-body { padding:14px 16px; display:flex; flex-direction:column; gap:12px; }
  .mt-test-note { font-size:12px; color:var(--ai-muted); margin:0; }
  .mt-test-row { display:flex; gap:14px; }
  .mt-test-row label { display:flex; flex-direction:column; gap:4px; font-size:11px; color:var(--ai-muted); font-weight:600; text-transform:uppercase; flex:1; }
  .mt-test-row input { font-size:13px; padding:6px 8px; border:1px solid var(--ai-line); border-radius:4px; outline:none; }
  .mt-test-row input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
  .mt-test-ftr { display:flex; gap:8px; padding:10px 16px; background:var(--ai-header-bg); border-top:1px solid var(--ai-line-soft); }
`;
